import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, FileText, User, LogOut, ChevronDown } from 'lucide-react';

const formatDate = (date) => new Intl.DateTimeFormat('en-US', {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
}).format(date);

export const TopBar = ({
  org,
  meta,
  rightSlot = null,
  user,
  notifications = [],
  showReport = false,
  onReportClick,
  showBrand = true,
  onProfileClick,
  onLogout,
  onNotificationsClick,
}) => {
  const [openUserMenu, setOpenUserMenu] = useState(false);
  const [openNotifications, setOpenNotifications] = useState(false);
  const [readIds, setReadIds] = useState(() => {
    try {
      const saved = localStorage.getItem('mitra_read_notifications');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem('mitra_read_notifications', JSON.stringify(readIds));
  }, [readIds]);
  const [notificationFilter, setNotificationFilter] = useState('all');
  const userRef = useRef(null);
  const notificationsRef = useRef(null);

  useEffect(() => {
    if (!openUserMenu) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpenUserMenu(false); };
    const onClick = (e) => {
      if (userRef.current && !userRef.current.contains(e.target)) setOpenUserMenu(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClick);
    };
  }, [openUserMenu]);

  useEffect(() => {
    if (!openNotifications) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpenNotifications(false); };
    const onClick = (e) => {
      if (notificationsRef.current && !notificationsRef.current.contains(e.target)) setOpenNotifications(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClick);
    };
  }, [openNotifications]);

  const unreadCount = notifications.filter((n) => !readIds.includes(n.id)).length;
  const filteredNotifications = notifications.filter((n) => {
    if (notificationFilter === 'unread') return !readIds.includes(n.id);
    return true;
  });

  const chipBase = {
    background: '#fff',
    borderColor: 'var(--theme-tertiary)',
  };
  const brandColor = 'var(--theme-primary)';

  return (
    <header className="relative z-[110] flex items-center justify-between gap-2 md:gap-4 px-3 md:px-4 lg:px-6 h-[52px] md:h-[56px] lg:h-[64px] shrink-0 bg-transparent">
      <div className="flex items-center gap-2.5 sm:gap-6 min-w-0 flex-1">
        {/* Brand mark — the M icon is always visible so mobile users see
            the product identity. The "Mitra POS" wordmark only renders
            when `showBrand` is true AND the viewport is sm+. When the
            sidebar is open at lg+, OrgDashboard sets showBrand=false to
            avoid duplicating the brand that already sits in the sidebar. */}
        <div
          className={`flex items-center gap-2.5 shrink-0 ${showBrand ? '' : 'lg:hidden'}`}
          aria-label={org?.name || "Mitra POS"}
        >
          <img
            src={org?.logo || "/logo.png"}
            alt={`${org?.name || 'Mitra POS'} Logo`}
            className="w-9 h-9 object-contain shrink-0"
          />
          <div className="leading-tight hidden sm:block max-w-[120px] md:max-w-[180px]">
            {org?.name ? (
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] truncate" style={{ color: brandColor }}>{org.name}</p>
            ) : (
              <>
                <p className="text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: brandColor }}>Mitra</p>
                <p className="text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: brandColor }}>POS</p>
              </>
            )}
          </div>
        </div>
        {meta && (
          <h1 className="font-extrabold tracking-tight text-[#0d0d0d] truncate text-base sm:text-xl md:text-2xl uppercase mt-0.5">
            {meta}
          </h1>
        )}
      </div>

      <div className="flex items-center justify-end gap-1.5 sm:gap-3 shrink-0">
        {rightSlot}
        {showReport && (
          <button
            type="button"
            onClick={onReportClick}
            style={chipBase}
            className="inline-flex items-center gap-2 border rounded-xl px-3 sm:px-4 py-2 text-xs font-semibold text-slate-700 transition-colors shadow-sm topbar-chip"
          >
            <span className="hidden sm:inline">Report</span>
            <FileText size={14} aria-hidden="true" />
          </button>
        )}
        
        <div ref={notificationsRef} className="relative">
          <button
            type="button"
            aria-label={unreadCount > 0 ? `${unreadCount} new notifications` : 'Notifications'}
            style={chipBase}
            onClick={() => setOpenNotifications((v) => !v)}
            className="relative w-10 h-10 rounded-full border flex items-center justify-center text-slate-700 transition-colors shadow-sm topbar-chip"
          >
            <Bell size={16} aria-hidden="true" className="pointer-events-none" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-xl bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center px-1 pointer-events-none">
                {unreadCount}
              </span>
            )}
          </button>

          <AnimatePresence>
            {openNotifications && (
              <motion.div
                role="menu"
                aria-label="Notifications menu"
                initial={{ opacity: 0, y: -6, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -6, scale: 0.98 }}
                transition={{ duration: 0.14 }}
                className="absolute right-0 top-full mt-2 w-80 bg-white rounded-2xl shadow-xl overflow-hidden z-[99999] border flex flex-col"
                style={{ borderColor: 'var(--theme-tertiary)', maxHeight: '400px' }}
              >
                <div className="px-4 pt-3 border-b flex flex-col gap-3 shrink-0" style={{ borderColor: 'var(--theme-tertiary)' }}>
                  <div className="flex items-center justify-between">
                    <p className="text-[12px] font-bold uppercase tracking-[0.12em] text-[#0d0d0d]">Notifications</p>
                    {unreadCount > 0 && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setReadIds((prev) => {
                            const next = [...prev];
                            notifications.forEach(n => { if (!next.includes(n.id)) next.push(n.id); });
                            return next;
                          });
                        }}
                        className="text-[10px] font-bold uppercase tracking-wider text-[var(--theme-primary)] hover:underline"
                      >
                        Mark all as read
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    <button 
                      type="button" 
                      onClick={() => setNotificationFilter('all')}
                      className={`text-[11px] font-semibold uppercase tracking-wider pb-2 border-b-2 transition-colors ${notificationFilter === 'all' ? 'text-[var(--theme-primary)] border-[var(--theme-primary)]' : 'text-slate-400 border-transparent hover:text-slate-600'}`}
                    >
                      All
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setNotificationFilter('unread')}
                      className={`text-[11px] font-semibold uppercase tracking-wider pb-2 border-b-2 transition-colors ${notificationFilter === 'unread' ? 'text-[var(--theme-primary)] border-[var(--theme-primary)]' : 'text-slate-400 border-transparent hover:text-slate-600'}`}
                    >
                      Unread {unreadCount > 0 && `(${unreadCount})`}
                    </button>
                  </div>
                </div>
                
                <div className="overflow-y-auto custom-scrollbar flex-1">
                  {filteredNotifications.length === 0 ? (
                    <div className="px-4 py-8 text-center flex flex-col items-center justify-center">
                      <Bell size={24} className="text-slate-200 mb-2" />
                      <span className="text-xs font-semibold text-slate-400">
                        {notificationFilter === 'unread' ? 'No unread notifications' : 'You are all caught up!'}
                      </span>
                    </div>
                  ) : (
                    <div className="divide-y divide-[var(--theme-tertiary)]">
                      {filteredNotifications.map((item) => {
                        const isUnread = !readIds.includes(item.id);
                        return (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => {
                              if (!readIds.includes(item.id)) {
                                setReadIds(prev => [...prev, item.id]);
                              }
                              setOpenNotifications(false);
                              if (onNotificationsClick) onNotificationsClick();
                            }}
                            className="w-full px-4 py-3 hover:bg-[var(--theme-secondary)] transition-colors flex items-start gap-2.5 text-left"
                          >
                            <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${isUnread ? 'bg-rose-500 animate-pulse' : 'bg-slate-300'}`} />
                            <div className="min-w-0">
                              <p className={`text-xs leading-snug ${isUnread ? 'font-semibold text-slate-700' : 'text-slate-500'}`}>{item.text}</p>
                              <p className={`text-[10px] font-bold mt-1 uppercase tracking-wider ${isUnread ? 'text-rose-500' : 'text-slate-400'}`}>Only {item.qty} items left</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {user && (
          <div ref={userRef} className="relative">
            <button
              type="button"
              onClick={() => setOpenUserMenu((v) => !v)}
              aria-expanded={openUserMenu}
              aria-haspopup="menu"
              style={chipBase}
              className="flex items-center gap-2 sm:gap-3 border rounded-full pl-1.5 pr-2 sm:pr-3 py-1.5 shadow-sm transition-colors outline-none topbar-chip"
            >
              <div
                className="w-8 h-8 rounded-full text-white text-xs font-bold flex items-center justify-center shrink-0 overflow-hidden"
                style={{ background: brandColor }}
                aria-hidden="true"
              >
                {user.avatarUrl ? (
                  <img src={user.avatarUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  user.initials
                )}
              </div>
              <div className="leading-tight text-left hidden md:block min-w-0">
                <p className="text-[13px] font-semibold text-[#0d0d0d] truncate">{user.name}</p>
                <p className="text-[11px] font-medium text-slate-500">{user.role}</p>
              </div>
              <ChevronDown
                size={14}
                className={`text-slate-400 transition-transform shrink-0 ${openUserMenu ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </button>

            <AnimatePresence>
              {openUserMenu && (
                <motion.div
                  role="menu"
                  aria-label="Account menu"
                  initial={{ opacity: 0, y: -6, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -6, scale: 0.98 }}
                  transition={{ duration: 0.14 }}
                  className="absolute right-0 top-full mt-2 w-52 bg-white rounded-2xl shadow-xl overflow-hidden z-[99999] border"
                  style={{ borderColor: 'var(--theme-tertiary)' }}
                >
                  <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--theme-tertiary)' }}>
                    <p className="text-[13px] font-semibold text-[#0d0d0d] truncate">{user.name}</p>
                    <p className="text-[11px] font-medium text-slate-500 truncate">{user.role}</p>
                  </div>
                  {onProfileClick && (
                    <button
                      role="menuitem"
                      type="button"
                      onClick={() => { setOpenUserMenu(false); onProfileClick(); }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-[0.1em] text-slate-700 transition-colors topbar-menu-item"
                    >
                      <User size={14} aria-hidden="true" style={{ color: brandColor }} />
                      My Profile
                    </button>
                  )}
                  {onLogout && (
                    <button
                      role="menuitem"
                      type="button"
                      onClick={() => { setOpenUserMenu(false); onLogout(); }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-[0.1em] text-rose-600 hover:bg-rose-50 transition-colors border-t"
                      style={{ borderColor: 'var(--theme-tertiary)' }}
                    >
                      <LogOut size={14} aria-hidden="true" />
                      Logout
                    </button>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>

      <style>{`
        .topbar-chip:hover { border-color: var(--theme-primary-tint); color: var(--theme-primary); }
        .topbar-chip:focus-visible { box-shadow: 0 0 0 4px var(--theme-primary-soft); outline: none; }
        .topbar-menu-item:hover { background: var(--theme-primary-soft); color: var(--theme-primary); }
      `}</style>
    </header>
  );
};

TopBar.formatDate = formatDate;

TopBar.propTypes = {
  meta: PropTypes.node,
  rightSlot: PropTypes.node,
  user: PropTypes.shape({
    name: PropTypes.string.isRequired,
    role: PropTypes.string.isRequired,
    initials: PropTypes.string.isRequired,
  }),
  notificationCount: PropTypes.number,
  notifications: PropTypes.array,
  showReport: PropTypes.bool,
  onReportClick: PropTypes.func,
  showBrand: PropTypes.bool,
  onProfileClick: PropTypes.func,
  onLogout: PropTypes.func,
  onNotificationsClick: PropTypes.func,
};
